<template>
  <a
    ref="homeNavigation"
    class="home-navigation inverted"
    :class="{
      'home-navigation-x': display === 'x',
      // 'home-navigation-hidden': !shouldDisplayInSession,
    }"
    @click="handleClick"
    @keydown.enter="handleClick"
    data-test="session-home-button"
  >
    <Modal
      v-if="display === 'log-off'"
      v-model="showModal"
      title="Log off"
      ok-text="Yes"
      cancel-text="Cancel"
      cancel-type="primary"
      ok-type="default"
      @hide="handleModalHide"
      size="sm"
      append-to-body
      data-test="log-off-modal"
    >
      <p>Are you sure you want to log off and leave the session?</p>
    </Modal>
    <div
      ref="homeNavigationCircle"
      class="home-navigation-circle"
      style="opacity: 0; visibility: hidden"
    ></div>
    <div ref="invertedHomeNavigationCircle" class="inverted-home-navigation-circle"></div>
    <span
      class="home-navigation-text"
      v-if="display === 'team'"
      style="position: relative; left: -10px"
      >My Team</span
    >

    <span
      v-else-if="display === 'log-off'"
      class="home-navigation-text"
      data-test="foundations-log-off"
      >Log off</span
    >
    <span v-else-if="display === 'home'" class="home-navigation-text" data-test="session-home-text"
      >Home</span
    >
  </a>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from "vue";
import { TimelineLite, TimelineMax } from "gsap";
import { Modal } from "uiv";
import { Analytics } from "../../services/analytics/analytics";
import logOut from "../../services/utilities/log-out";

const props = defineProps<{
  display?: "home" | "team" | "x" | "log-off";
  closeUrl?: string;
}>();

const showModal = ref(false);

const instance: any = getCurrentInstance()?.proxy;

const session = computed(() => instance?.$parent);
const display = computed(() => props.display ?? "home");
const closeUrl = computed(() => props.closeUrl ?? "/");

function sendAnalytics(): void {
  Analytics.action("Cancelled Session", "SessionActions", {
    sessionId: session.value.getSessionId(),
    sessionVersion: session.value.getSessionVersion(),
  });
}

function handleClick(): void {
  if (props.display === "log-off") {
    showModal.value = true;
  } else {
    session.value.isLoading = true;
    sendAnalytics();
    setTimeout(() => {
      window.location.href = closeUrl.value;
    }, 2000);
  }
}

function handleModalHide(msg: "ok" | "cancel"): void {
  if (msg === "ok") {
    session.value.isLoading = true;
    sendAnalytics();
    logOut();
  }
}

const colorSwitch = (tl: TimelineMax): TimelineLite =>
  tl
    .to(
      instance.$refs.homeNavigationCircle,
      1 * 0.2,
      {
        autoAlpha: 1,
        ease: session.value.titleVisualEase,
      },
      1,
    )
    .to(
      instance.$refs.invertedHomeNavigationCircle,
      1 * 0.2,
      {
        autoAlpha: 0,
        ease: session.value.titleVisualEase,
      },
      1,
    )
    .to(
      instance.$refs.homeNavigation,
      1 * 0.2,
      {
        css: { className: "-=inverted" },
        autoCss: true,
        ease: session.value.titleVisualEase,
      },
      1,
    );

const colorRevert = (tl: TimelineMax): TimelineLite =>
  tl
    .to(
      instance.$refs.homeNavigationCircle,
      1 * 0.2,
      {
        autoAlpha: 0,
        ease: session.value.titleVisualEase,
      },
      1,
    )
    .to(
      instance.$refs.invertedHomeNavigationCircle,
      1 * 0.2,
      {
        autoAlpha: 1,
        ease: session.value.titleVisualEase,
      },
      1,
    )
    .to(
      instance.$refs.homeNavigation,
      1 * 0.2,
      {
        css: { className: "+=inverted" },
        autoCss: true,
        ease: session.value.titleVisualEase,
      },
      1,
    );

const fadeOut = (tl: TimelineMax): TimelineLite =>
  tl.to(instance.$refs.homeNavigation, 1 * 0.2, {
    autoAlpha: 0,
  });

const fadeIn = (tl: TimelineMax): TimelineLite =>
  tl.to(instance.$refs.homeNavigation, 1 * 0.2, {
    autoAlpha: 1,
  });

defineExpose({
  colorSwitch,
  colorRevert,
  fadeOut,
  fadeIn,
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<!--<style lang="less" scoped>-->
<!--.home-navigation-hidden {-->
<!--  display: none !important;-->
<!--  visibility: hidden !important;-->
<!--}-->
<!--</style>-->
